import { FC } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import {
  SyncRow,
  SyncableField,
  isActionable
} from "../../components/lifecycle/warehouseSync/utils";
import { PreviewRow, Selection, acceptedFor, rowKey } from "./rows";

type Props = {
  rows: PreviewRow[];
  selection: Selection;
  disabled: boolean;
  onToggleField: (row: PreviewRow, field: SyncableField) => void;
  onToggleRow: (row: PreviewRow, accept: boolean) => void;
};

const ENTITY_LABELS: Record<PreviewRow["entityType"], string> = {
  counterparty: "Counterparty",
  broker: "Broker",
  country: "Country"
};

// The four syncable fields, in the order the sync dialog shows them.
const FIELDS: { field: SyncableField; label: string }[] = [
  { field: "kycCheck", label: "KYC check" },
  { field: "creditCheck", label: "Credit check" },
  { field: "creditAmount", label: "Credit amount" },
  { field: "creditCurrency", label: "Credit currency" }
];

/**
 * One field of one counterparty. Four states worth telling apart: no row from that warehouse source
 * at all, a real difference (the only case with a checkbox), already in sync, and a warehouse value
 * the Hub has no way to store.
 */
const FieldCell: FC<{
  row: SyncRow | undefined;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}> = ({ row, checked, disabled, onToggle }) => {
  if (!row) {
    return (
      <TableCell>
        <Typography variant="caption" color="text.disabled">
          No data
        </Typography>
      </TableCell>
    );
  }

  if (!isActionable(row)) {
    return (
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {row.warehouseDisplay}
        </Typography>
        {row.syncable ? (
          <Typography variant="caption" color="text.secondary" display="block">
            In sync
          </Typography>
        ) : (
          // The label says which of these it is and the tooltip carries the sentence. It used to say
          // "Cannot sync" in amber for every one of them, which read as a fault even for the common
          // and entirely normal case of an unlimited credit line, and sent people to ask Risk why
          // their data would not sync. Only a genuine "attention" row is coloured now.
          <Tooltip title={row.unsyncable?.reason ?? ""}>
            <Typography
              variant="caption"
              display="block"
              color={
                row.unsyncable?.kind === "attention"
                  ? "warning.main"
                  : "text.secondary"
              }
            >
              {row.unsyncable?.label ?? "Nothing to copy"}
            </Typography>
          </Tooltip>
        )}
      </TableCell>
    );
  }

  return (
    <TableCell>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Checkbox
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
          sx={{ padding: 0.5 }}
        />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {row.hubDisplay} {"->"}
          </Typography>
          <Typography variant="body2">{row.warehouseDisplay}</Typography>
        </Box>
      </Box>
    </TableCell>
  );
};

export const SyncTable: FC<Props> = ({
  rows,
  selection,
  disabled,
  onToggleField,
  onToggleRow
}) => (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" />
          <TableCell>Name</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Catalyst ID</TableCell>
          <TableCell>Last synced</TableCell>
          {FIELDS.map(({ field, label }) => (
            <TableCell key={field}>{label}</TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row) => {
          const accepted = acceptedFor(selection, row);
          const allAccepted =
            row.actionable.length > 0 &&
            accepted.size === row.actionable.length;

          return (
            <TableRow key={rowKey(row)} hover>
              <TableCell padding="checkbox">
                {row.actionable.length > 0 ? (
                  <Checkbox
                    size="small"
                    checked={allAccepted}
                    indeterminate={accepted.size > 0 && !allAccepted}
                    disabled={disabled}
                    onChange={() => onToggleRow(row, !allAccepted)}
                  />
                ) : null}
              </TableCell>

              <TableCell>
                <Typography variant="body2">{row.name}</Typography>
                {/* Countries appear once per party, so the name alone does not identify the row.
                    display="block" because caption renders as a span, which otherwise runs straight
                    on from the name: "UkraineTSO (GTSOU)". */}
                {row.partyLabel ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {row.partyLabel}
                  </Typography>
                ) : null}
              </TableCell>

              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={ENTITY_LABELS[row.entityType]}
                />
              </TableCell>

              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {row.catalystId}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {row.syncedAt ? dayjs(row.syncedAt).format("ll") : "Never"}
                </Typography>
              </TableCell>

              {row.foundInWarehouse ? (
                FIELDS.map(({ field }) => (
                  <FieldCell
                    key={field}
                    row={row.rows.find((r) => r.field === field)}
                    checked={accepted.has(field)}
                    disabled={disabled}
                    onToggle={() => onToggleField(row, field)}
                  />
                ))
              ) : (
                <TableCell colSpan={FIELDS.length}>
                  <Typography variant="caption" color="warning.main">
                    This Catalyst ID matches no warehouse record. Check the id
                    on the Compliance lifecycle.
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);
